import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Order } from '@api/Orders/Order';
import { isOrderLineV2 } from '@api/Orders/OrderLine';
import { DataGrid, esES, GridColDef, GridToolbar } from '@mui/x-data-grid';
import InputQuantity from '@components/SalesReport/SalesByInputs/InputQuantity';
interface SalesByInputsProps {
    orders: Order[];
}
interface SalesByInputs {
    id: string;
    name: string;
    count: number;
}
const SalesByInputs: React.FunctionComponent<SalesByInputsProps> = (props) => {
    const { orders } = props;
    const [products, setProducts] = useState<SalesByInputs[]>([]);

    useEffect(() => {
        const array: SalesByInputs[] = [];

        const addToArray = (id: string, name: string, quantity: number) => {
            const index = array.findIndex((item) => item.id === id);
            if (index === -1) {
                array.push({ id, name, count: quantity });
            } else {
                array[index].count += quantity;
            }
        };

        orders.forEach((order) => {
            // ÓRDENES v2: el consumo EXACTO ya viene calculado por el motor de
            // inventario (F5) y congelado en la orden — explosión de recetas,
            // modificadores e ingredientes quitados incluidos. Se lee de ahí en vez
            // de reconstruirlo aquí: la explosión vive una sola vez, en el server.
            // Las cantidades del ledger son NEGATIVAS (son salidas) → se voltean.
            const consumption = order.inventory?.consumption;
            if (consumption?.length) {
                consumption.forEach((item) => {
                    if (item?.inputId) {
                        addToArray(item.inputId, item.inputName || 'Sin nombre', Math.abs(item.quantity || 0));
                    }
                });
                // Una orden v2 se resuelve completa por aquí; si además trae líneas
                // v1 (imposible hoy, pero el schema lo permite) siguen abajo
                if ((order.products || []).every((line) => !line || isOrderLineV2(line))) {
                    return;
                }
            }
            order.products.forEach((product) => {
                if (product) {
                    // Una línea v2 SIN consumo persistido no se puede desglosar: su
                    // receta no vive en la línea. Pasa con órdenes creadas antes de
                    // que el motor registrara el consumo (se pueden rellenar con
                    // `scripts/backfill-consumo.mjs`).
                    if (isOrderLineV2(product)) {
                        return;
                    }
                    product.inputs.forEach((input) => {
                        addToArray(
                            input.inputId,
                            input.inputData?.name || 'Sin nombre (Posiblemente eliminado)',
                            input.quantity
                        );
                    });
                    product.variables.forEach((variable) => {
                        if (variable.selectionOptions?.input.inputId) {
                            addToArray(
                                variable.selectionOptions.input.inputId,
                                variable.selectionOptions.option,
                                variable.selectionOptions.input.quantity * variable.quantity
                            );
                        }
                        if (variable.extraIngredients?.input.inputId) {
                            addToArray(
                                variable.extraIngredients.input.inputId,
                                variable.extraIngredients.ingredient,
                                variable.extraIngredients.input.quantity * variable.quantity
                            );
                        }
                    });
                }
            });
        });
        setProducts(array);
    }, [orders]);

    const columns = useMemo<GridColDef<SalesByInputs>[]>(
        () => [
            {
                field: 'name',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Producto
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.name}</Typography>,
                valueGetter: ({ row }) => row.name
            },
            {
                field: 'count',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Vendidos
                    </Typography>
                ),
                renderCell: ({ row }) => <InputQuantity inputId={row.id} quantity={row.count} />,
                valueGetter: ({ row }) => row.count
            }
        ],
        []
    );

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGrid
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                sx={{ borderWidth: 0 }}
                rows={products}
                columns={columns}
                slots={{
                    toolbar: GridToolbar
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 }
                    }
                }}
            />
        </Box>
    );
};

export default SalesByInputs;
